import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Edit2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListLockersTable = ({ token, clients, onClientsUpdate }) => {
  const [mailboxes, setMailboxes] = useState([]);
  const [shippingRates, setShippingRates] = useState([]);
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [clientTrackings, setClientTrackings] = useState([]);
  const [clientRepacks, setClientRepacks] = useState([]);
  const [clientWRs, setClientWRs] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resetPasswordModal, setResetPasswordModal] = useState({ open: false, client: null });
  const [newPassword, setNewPassword] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    search: '',
    desde: '',
    hasta: '',
    agencia: ''
  });

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const fetchData = async () => {
    try {
      const [mailboxesRes, ratesRes] = await Promise.all([
        axios.get(`${API}/admin/mailboxes`, axiosConfig),
        axios.get(`${API}/admin/shipping-rates`, axiosConfig)
      ]);
      setMailboxes(mailboxesRes.data);
      setShippingRates(ratesRes.data);
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchClientDetails = async (clientId) => {
    try {
      const [invoicesRes, paymentsRes, trackingsRes, repacksRes, wrsRes] = await Promise.all([
        axios.get(`${API}/admin/invoices`, axiosConfig),
        axios.get(`${API}/admin/payments`, axiosConfig),
        axios.get(`${API}/admin/trackings`, axiosConfig),
        axios.get(`${API}/admin/repacks`, axiosConfig),
        axios.get(`${API}/admin/warehouse-receipts`, axiosConfig)
      ]);
      
      const clientInv = invoicesRes.data.filter(inv => inv.client_id === clientId);
      const clientPay = paymentsRes.data.filter(pay => pay.client_id === clientId);
      const clientTrack = trackingsRes.data.filter(t => t.client_id === clientId);
      const clientRep = repacksRes.data.filter(r => r.client_id === clientId);
      const clientWR = wrsRes.data.filter(wr => wr.client_id === clientId);
      
      setClientInvoices(clientInv);
      setClientPayments(clientPay);
      setClientTrackings(clientTrack);
      setClientRepacks(clientRep);
      setClientWRs(clientWR);
    } catch (error) {
    }
  };

  const toggleClientExpand = async (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      await fetchClientDetails(clientId);
    }
  };

  const getClientInfo = (clientId) => {
    return clients.find(c => c.id === clientId) || {};
  };

  const getRateName = (rateId) => {
    if (!rateId) return 'N/A';
    const rate = shippingRates.find(r => r.id === rateId);
    return rate ? `${rate.shipping_type} - ${rate.customer_type}` : 'N/A';
  };

  const filteredMailboxes = mailboxes.filter(mailbox => {
    const client = getClientInfo(mailbox.client_id);
    const matchesSearch = !searchFilters.search || 
      client.full_name?.toLowerCase().includes(searchFilters.search.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchFilters.search.toLowerCase()) ||
      mailbox.locker_id?.toLowerCase().includes(searchFilters.search.toLowerCase());
    
    return matchesSearch;
  });

  // Handle select/deselect all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allClientIds = filteredMailboxes.map(m => getClientInfo(m.client_id)?.id).filter(Boolean);
      setSelectedClients(allClientIds);
    } else {
      setSelectedClients([]);
    }
  };

  // Handle individual selection
  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Handle batch delete
  const handleBatchDelete = async () => {
    if (selectedClients.length === 0) {
      alert('Seleccione al menos un cliente para eliminar');
      return;
    }
    
    if (!window.confirm(`¿Está seguro de eliminar ${selectedClients.length} cliente(s)? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.post(`${API}/admin/clients/batch-delete`, {
        client_ids: selectedClients
      }, axiosConfig);
      
      alert(`✅ ${selectedClients.length} cliente(s) eliminado(s) exitosamente`);
      setSelectedClients([]);
      fetchData();
      if (onClientsUpdate) onClientsUpdate();
    } catch (error) {
      alert('Error al eliminar clientes: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Open edit modal
  const openEditModal = (client) => {
    setEditingClient(client);
    setEditForm({
      full_name: client.full_name || '',
      email: client.email || '',
      phone: client.phone || '',
      phone_country_code: client.phone_country_code || '+58',
      id_document: client.id_document || '',
      customer_type: client.customer_type || 'publico',
      delivery_address: client.delivery_address || '',
      delivery_city: client.delivery_city || '',
      delivery_state: client.delivery_state || '',
      is_active: client.is_active !== false
    });
  };

  // Save edited client
  const handleSaveEdit = async () => {
    if (!editingClient) return;

    try {
      await axios.put(`${API}/admin/clients/${editingClient.id}`, editForm, axiosConfig);
      alert('✅ Cliente actualizado exitosamente');
      setEditingClient(null);
      fetchData();
      if (onClientsUpdate) onClientsUpdate();
    } catch (error) {
      alert('Error al actualizar cliente: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Reset client password
  const handleResetPassword = async () => {
    if (!resetPasswordModal.client || !newPassword) {
      alert('Por favor ingrese una nueva contraseña');
      return;
    }

    if (newPassword.length < 4) {
      alert('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/clients/${resetPasswordModal.client.id}/reset-password`,
        { new_password: newPassword },
        axiosConfig
      );
      alert(`✅ Contraseña actualizada exitosamente para ${resetPasswordModal.client.email}`);
      setResetPasswordModal({ open: false, client: null });
      setNewPassword('');
    } catch (error) {
      alert('Error al resetear contraseña: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Open reset password modal
  const openResetPasswordModal = (client) => {
    setResetPasswordModal({ open: true, client });
    setNewPassword(client.id_document || ''); // Pre-fill with cedula
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border-b pb-4">
        <h1 className="text-2xl font-light text-gray-700 mb-4">Lista de Casilleros</h1>
        <nav className="flex text-sm text-gray-500">
          <span className="text-blue-600 hover:underline cursor-pointer">Home</span>
          <span className="mx-2">/</span>
          <span className="text-blue-600 hover:underline cursor-pointer">Crear Casillero</span>
          <span className="mx-2">/</span>
          <span className="text-blue-600 hover:underline cursor-pointer">Listar Casilleros</span>
          <span className="mx-2">/</span>
          <span>Lista de Casilleros</span>
        </nav>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Búsqueda</Label>
              <Input
                placeholder="Nombre,Cliente,Email..."
                value={searchFilters.search}
                onChange={(e) => setSearchFilters({...searchFilters, search: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Desde</Label>
              <Input
                type="date"
                value={searchFilters.desde}
                onChange={(e) => setSearchFilters({...searchFilters, desde: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Hasta</Label>
              <Input
                type="date"
                value={searchFilters.hasta}
                onChange={(e) => setSearchFilters({...searchFilters, hasta: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Agencia</Label>
              <Select value={searchFilters.agencia} onValueChange={(v) => setSearchFilters({...searchFilters, agencia: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="traetelodafuera">Traetelodafuera</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedClients.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-blue-700 font-medium">
            {selectedClients.length} cliente(s) seleccionado(s)
          </span>
          <div className="flex gap-2">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleBatchDelete}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Eliminar Seleccionados
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSelectedClients([])}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedClients.length === filteredMailboxes.length && filteredMailboxes.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left p-3 font-semibold text-gray-700">Número</th>
                <th className="text-left p-3 font-semibold text-gray-700">Cliente</th>
                <th className="text-left p-3 font-semibold text-gray-700">Fecha de Creación</th>
                <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                <th className="text-left p-3 font-semibold text-gray-700">Ciudad</th>
                <th className="text-left p-3 font-semibold text-gray-700">Estado</th>
                <th className="text-left p-3 font-semibold text-gray-700">País</th>
                <th className="text-left p-3 font-semibold text-gray-700">Estatus</th>
                <th className="text-left p-3 font-semibold text-gray-700">Agencia</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tarifa</th>
                <th className="text-left p-3 font-semibold text-gray-700">Ubicación</th>
                <th className="text-left p-3 font-semibold text-gray-700">Teléfono</th>
                <th className="text-left p-3 font-semibold text-gray-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredMailboxes.map((mailbox) => {
                const client = getClientInfo(mailbox.client_id);
                const isExpanded = expandedClient === client.id;
                const totalInvoiced = clientInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
                const totalPaid = clientPayments.filter(p => p.status === 'verified').reduce((sum, p) => sum + (p.amount || 0), 0);
                const pendingBalance = totalInvoiced - totalPaid;

                return (
                  <React.Fragment key={mailbox.id}>
                    <tr className={`border-b hover:bg-gray-50 ${selectedClients.includes(client.id) ? 'bg-blue-50' : ''}`}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleSelectClient(client.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="p-3">
                        <span className="inline-block bg-green-500 text-white px-3 py-1 rounded text-xs font-mono">
                          {mailbox.locker_id}
                        </span>
                      </td>
                      <td className="p-3">
                        <button 
                          onClick={() => toggleClientExpand(client.id)}
                          className="text-blue-600 hover:underline font-semibold"
                        >
                          {client.full_name || 'N/A'} {isExpanded ? '▼' : '▶'}
                        </button>
                      </td>
                    <td className="p-3 text-gray-600">
                      {new Date(client.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.email || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.delivery_city || client.city || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.delivery_state || client.state || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600">
                      Venezuela
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        client.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {client.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600">
                      Traetelodafuera_miami
                    </td>
                    <td className="p-3 text-gray-600">
                      {getRateName(mailbox.shipping_rate_id) || client.customer_type || 'público'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.delivery_address || client.address || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.phone_country_code && client.phone 
                        ? `${client.phone_country_code} ${client.phone}` 
                        : 'N/A'}
                    </td>
                    <td className="p-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(client)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>

                  {/* Expanded Client Details */}
                  {isExpanded && (
                    <tr>
                      <td colSpan="14" className="p-0">
                        <div className="bg-gray-50 border-t-2 border-blue-500 p-6">
                          {/* Header with Client Name and Actions */}
                          <div className="flex justify-between items-center mb-6 pb-4 border-b">
                            <div>
                              <h3 className="text-xl font-bold text-gray-800">{client.full_name}</h3>
                              <p className="text-sm text-gray-600">Casillero: {mailbox.locker_id}</p>
                            </div>
                            <div className="flex gap-2">
                              <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm">
                                Estado de Cuenta
                              </button>
                              <button className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm">
                                Ajuste de Balance
                              </button>
                              <button 
                                onClick={() => openResetPasswordModal(client)}
                                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm"
                              >
                                🔑 Resetear Contraseña
                              </button>
                            </div>
                          </div>

                          {/* Client Data Grid */}
                          <div className="grid grid-cols-3 gap-6 mb-6">
                            {/* Datos del Cliente */}
                            <div className="bg-white p-4 rounded border">
                              <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Datos del Cliente</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Email:</span>
                                  <span className="font-semibold">{client.email}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Teléfono:</span>
                                  <span className="font-semibold">{client.phone_country_code} {client.phone || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Ciudad:</span>
                                  <span className="font-semibold">{client.city || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Documento:</span>
                                  <span className="font-semibold">{client.id_document || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Tipo:</span>
                                  <span className="font-semibold">{client.account_type || 'Persona'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Financial Summary */}
                            <div className="bg-white p-4 rounded border">
                              <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Resumen Financiero</h4>
                              <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Total Facturado:</span>
                                  <span className="font-bold text-blue-600 text-lg">${totalInvoiced.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Total Pagado:</span>
                                  <span className="font-bold text-green-600 text-lg">${totalPaid.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t">
                                  <span className="text-gray-700 font-semibold">Saldo Pendiente:</span>
                                  <span className={`font-bold text-xl ${pendingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    ${pendingBalance.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Statistics */}
                            <div className="bg-white p-4 rounded border">
                              <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">Estadísticas</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Paquetes:</span>
                                  <span className="font-semibold text-blue-600">{clientTrackings.length}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Reempaques:</span>
                                  <span className="font-semibold text-purple-600">{clientRepacks.length}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Warehouse Receipts:</span>
                                  <span className="font-semibold text-orange-600">{clientWRs.length}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Facturas:</span>
                                  <span className="font-semibold">{clientInvoices.length}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Pagos Verificados:</span>
                                  <span className="font-semibold text-green-600">
                                    {clientPayments.filter(p => p.status === 'verified').length}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Paquetes del Cliente */}
                          {clientTrackings.length > 0 && (
                            <div className="bg-white p-4 rounded border mb-4">
                              <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">📦 Paquetes del Cliente</h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="text-left p-2 text-gray-600">Tracking</th>
                                      <th className="text-left p-2 text-gray-600">Descripción</th>
                                      <th className="text-left p-2 text-gray-600">Estado</th>
                                      <th className="text-left p-2 text-gray-600">Peso (lb)</th>
                                      <th className="text-left p-2 text-gray-600">Fecha</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clientTrackings.map(tracking => (
                                      <tr key={tracking.id} className="border-t">
                                        <td className="p-2 font-mono text-xs">{tracking.tracking_number}</td>
                                        <td className="p-2">{tracking.description || 'N/A'}</td>
                                        <td className="p-2">
                                          <span className={`px-2 py-1 rounded text-xs ${
                                            tracking.status === 'received' ? 'bg-green-100 text-green-800' :
                                            tracking.status === 'pending_reception' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {tracking.status === 'received' ? 'Recibido' :
                                             tracking.status === 'pending_reception' ? 'Pendiente Recepción' :
                                             tracking.status}
                                          </span>
                                        </td>
                                        <td className="p-2">{tracking.weight || 0} lb</td>
                                        <td className="p-2">{new Date(tracking.created_at).toLocaleDateString('es-ES')}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Direcciones */}
                          <div className="bg-white p-4 rounded border mb-4">
                            <h4 className="font-semibold text-gray-700 mb-3">Direcciones</h4>
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="text-left p-2">Nombre</th>
                                  <th className="text-left p-2">Dirección</th>
                                  <th className="text-left p-2">Ciudad</th>
                                  <th className="text-left p-2">Teléfono</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="p-2">{client.full_name}</td>
                                  <td className="p-2">{client.address || 'N/A'}</td>
                                  <td className="p-2">{client.city || 'N/A'}</td>
                                  <td className="p-2">{client.phone_country_code} {client.phone || 'N/A'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Facturas */}
                          <div className="bg-white p-4 rounded border mb-4">
                            <h4 className="font-semibold text-gray-700 mb-3">Facturas ({clientInvoices.length})</h4>
                            {clientInvoices.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="text-left p-2">Número</th>
                                      <th className="text-left p-2">Fecha</th>
                                      <th className="text-left p-2">Monto</th>
                                      <th className="text-left p-2">Estado</th>
                                      <th className="text-left p-2">WR</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clientInvoices.slice(0, 5).map(invoice => (
                                      <tr key={invoice.id} className="border-b">
                                        <td className="p-2 font-mono text-xs">{invoice.invoice_number}</td>
                                        <td className="p-2">{new Date(invoice.created_at).toLocaleDateString()}</td>
                                        <td className="p-2 font-semibold text-green-600">${invoice.total?.toFixed(2) || '0.00'}</td>
                                        <td className="p-2">
                                          <span className={`px-2 py-1 rounded text-xs ${
                                            invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                          }`}>
                                            {invoice.status === 'paid' ? 'Pagada' : 'Pendiente'}
                                          </span>
                                        </td>
                                        <td className="p-2 text-xs">{invoice.wr_number || 'N/A'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No hay facturas</p>
                            )}
                          </div>

                          {/* Pagos */}
                          <div className="bg-white p-4 rounded border">
                            <h4 className="font-semibold text-gray-700 mb-3">Historial de Pagos ({clientPayments.length})</h4>
                            {clientPayments.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="text-left p-2">Fecha</th>
                                      <th className="text-left p-2">Monto</th>
                                      <th className="text-left p-2">Método</th>
                                      <th className="text-left p-2">Referencia</th>
                                      <th className="text-left p-2">Estado</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clientPayments.slice(0, 5).map(payment => (
                                      <tr key={payment.id} className="border-b">
                                        <td className="p-2">{new Date(payment.payment_date).toLocaleDateString()}</td>
                                        <td className="p-2 font-semibold text-blue-600">${payment.amount.toFixed(2)}</td>
                                        <td className="p-2">{payment.payment_method}</td>
                                        <td className="p-2 font-mono text-xs">{payment.reference_number}</td>
                                        <td className="p-2">
                                          <span className={`px-2 py-1 rounded text-xs ${
                                            payment.status === 'verified' ? 'bg-green-100 text-green-800' : 
                                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                            {payment.status === 'verified' ? 'Verificado' : 
                                             payment.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No hay pagos registrados</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredMailboxes.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron casilleros
          </div>
        )}
      </div>

      {/* Edit Client Modal */}
      {editingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Editar Cliente</h2>
              <button 
                onClick={() => setEditingClient(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre Completo</Label>
                <Input
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Código País</Label>
                <Select 
                  value={editForm.phone_country_code} 
                  onValueChange={(v) => setEditForm({...editForm, phone_country_code: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+58">+58 VE</SelectItem>
                    <SelectItem value="+1">+1 US</SelectItem>
                    <SelectItem value="+57">+57 CO</SelectItem>
                    <SelectItem value="+34">+34 ES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Documento ID / Cédula</Label>
                <Input
                  value={editForm.id_document}
                  onChange={(e) => setEditForm({...editForm, id_document: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tipo de Cliente</Label>
                <Select 
                  value={editForm.customer_type} 
                  onValueChange={(v) => setEditForm({...editForm, customer_type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publico">Público</SelectItem>
                    <SelectItem value="alumno">Alumnos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Dirección de Entrega</Label>
                <Input
                  value={editForm.delivery_address}
                  onChange={(e) => setEditForm({...editForm, delivery_address: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Ciudad de Entrega</Label>
                <Input
                  value={editForm.delivery_city}
                  onChange={(e) => setEditForm({...editForm, delivery_city: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Estado de Entrega</Label>
                <Input
                  value={editForm.delivery_state}
                  onChange={(e) => setEditForm({...editForm, delivery_state: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <span>Cliente Activo</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setEditingClient(null)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit}>
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">🔑 Resetear Contraseña</h2>
              <button 
                onClick={() => {
                  setResetPasswordModal({ open: false, client: null });
                  setNewPassword('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Cliente:</strong> {resetPasswordModal.client?.full_name}
                </p>
                <p className="text-sm text-blue-800">
                  <strong>Email:</strong> {resetPasswordModal.client?.email}
                </p>
                <p className="text-sm text-blue-800">
                  <strong>Cédula:</strong> {resetPasswordModal.client?.id_document || 'N/A'}
                </p>
              </div>
              
              <div>
                <Label>Nueva Contraseña</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Ingrese la nueva contraseña"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: La cédula del cliente está pre-cargada. Puede usarla o cambiarla.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setResetPasswordModal({ open: false, client: null });
                  setNewPassword('');
                }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleResetPassword}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Resetear Contraseña
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListLockersTable;
